import type { RefObject } from "react";
import { Checkbox, Col, Divider, Form, Input, Radio, Row, Typography } from "antd";
import type { FormInstance } from "antd";
import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import { getSelectMetadataByCode, type ProductCompanyListVO } from "@ifa/utils";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import { ProductSelectInput } from "../../components/ProductSelectInput";
import { SelectInput } from "../../components/form/SelectInput";
import {
  getLegacyQuestionValueFromList,
  updateLegacyQuestionList,
} from "../order-fna/fnaQuestionAccess.js";
import styles from "./FinancialForm.module.css";
import { LegacyFinancialAnalysisSection } from "./LegacyFinancialAnalysisSection";
import type { ChooseProduct, OrderFinancialVO } from "./orders";

const { Text } = Typography;

type QuestionValueType = "RADIO" | "CHECKBOX" | "INPUT";
type QuestionSection =
  | "financialAnalysis"
  | "transferInsuranceDeclaration"
  | "suggestionsAndReasons";

type LegacyFnaFormProps = {
  form: FormInstance;
  readOnly?: boolean;
  className?: string;
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  companyList: ProductCompanyListVO[];
  productSectionRef: RefObject<HTMLDivElement | null>;
  productValidation: Record<
    number,
    { paymentTermValid: boolean; benefitTermValid: boolean }
  >;
  onOpenSelector?: (
    context: { type: string; index: number },
    productType?: string,
  ) => void;
  onMessageError: (content: string) => void;
  handleChange: (field: keyof OrderFinancialVO | string, value: any) => void;
  policyholderBirthDate?: string;
  intendedRetirementAge?: string;
  onAddPremiumFinancing: () => void;
  onRemovePremiumFinancing: (index: number) => void;
  onPremiumFinancingChange: (
    index: number,
    field: string,
    value: any,
  ) => void;
  monthlyDisposableIncome: number;
  yearlyDisposableIncome: number;
  monthlyDisposableIncomePremiumLimit: number;
  yearlyDisposableIncomePremiumLimit: number;
  liquidAssetsAmount: number;
  liquidAssetsPremiumLimit: number;
  formatCurrency: (value: number) => string;
  formatInteger: (value: number) => string;
};

const newChooseProduct: ChooseProduct = {
  productCode: null,
  choose: false,
};

export function LegacyFnaForm({
  form,
  readOnly = false,
  className,
  data,
  onChange,
  companyList,
  productSectionRef,
  productValidation,
  onOpenSelector,
  onMessageError,
  handleChange,
  policyholderBirthDate,
  intendedRetirementAge,
  onAddPremiumFinancing,
  onRemovePremiumFinancing,
  onPremiumFinancingChange,
  monthlyDisposableIncome,
  yearlyDisposableIncome,
  monthlyDisposableIncomePremiumLimit,
  yearlyDisposableIncomePremiumLimit,
  liquidAssetsAmount,
  liquidAssetsPremiumLimit,
  formatCurrency,
  formatInteger,
}: LegacyFnaFormProps) {
  const getQuestionValue = (
    section: QuestionSection,
    questionKey: string,
    type: QuestionValueType = "RADIO",
  ) =>
    getLegacyQuestionValueFromList(
      data[section] as any[] | undefined,
      questionKey,
      type,
    );

  const updateQuestionValue = (
    section: QuestionSection,
    questionKey: string,
    value: any,
    type: QuestionValueType = "RADIO",
  ) => {
    onChange({
      ...data,
      [section]: updateLegacyQuestionList(
        data[section] as any[] | undefined,
        questionKey,
        value,
        type,
      ),
    });
  };

  const calculateAge = (birthDate: string | undefined): number | null => {
    if (!birthDate) return null;

    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }

    return age;
  };

  const shouldShowQuestion42 = (): boolean => {
    if (!intendedRetirementAge) {
      return false;
    }

    if (intendedRetirementAge === "RETIRED" || intendedRetirementAge === "OTHERS") {
      return true;
    }

    const willingPremiumPaymentPeriodRange = getQuestionValue(
      "financialAnalysis",
      "willingPremiumPaymentPeriodRange",
      "RADIO",
    );

    if (!willingPremiumPaymentPeriodRange) {
      return false;
    }

    if (willingPremiumPaymentPeriodRange === "E" || willingPremiumPaymentPeriodRange === "F") {
      return true;
    }

    if (["A", "B", "C", "D"].includes(willingPremiumPaymentPeriodRange)) {
      const currentAge = calculateAge(policyholderBirthDate);
      if (currentAge === null) {
        return false;
      }

      const retirementAgeMetadata = getSelectMetadataByCode("INTENDED_RETIREMENT_AGE_OPTION");
      const retirementAgeItem = retirementAgeMetadata?.items?.find(
        (item) => item.key === intendedRetirementAge,
      );

      if (!retirementAgeItem?.key) {
        return false;
      }

      const ageMatch = retirementAgeItem.key.match(/\d+/);
      const retirementAge = ageMatch ? Number.parseInt(ageMatch[0], 10) : null;

      if (retirementAge == null || Number.isNaN(retirementAge)) {
        return false;
      }

      const maxYearsMap: Record<string, number> = {
        A: 5,
        B: 10,
        C: 15,
        D: 20,
      };

      return currentAge + maxYearsMap[willingPremiumPaymentPeriodRange] > retirementAge;
    }

    return false;
  };

  const setSingleChoiceQuestionInSection = (
    section: "transferInsuranceDeclaration" | "suggestionsAndReasons",
    questionKey: string,
    newValue: string,
  ) =>
    updateLegacyQuestionList(
      data[section] as any[] | undefined,
      questionKey,
      newValue,
      "RADIO",
    );

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={readOnly}
      className={className}
    >
      <LegacyFinancialAnalysisSection
        data={data}
        form={form}
        onChange={onChange}
        getQuestionValue={(questionKey, type = "RADIO") =>
          getQuestionValue("financialAnalysis", questionKey, type)
        }
        updateQuestionValue={(questionKey, value, type = "RADIO") =>
          updateQuestionValue("financialAnalysis", questionKey, value, type)
        }
        shouldShowQuestion42={shouldShowQuestion42}
        monthlyDisposableIncome={monthlyDisposableIncome}
        yearlyDisposableIncome={yearlyDisposableIncome}
        monthlyDisposableIncomePremiumLimit={
          monthlyDisposableIncomePremiumLimit
        }
        yearlyDisposableIncomePremiumLimit={
          yearlyDisposableIncomePremiumLimit
        }
        liquidAssetsAmount={liquidAssetsAmount}
        liquidAssetsPremiumLimit={liquidAssetsPremiumLimit}
        formatCurrency={formatCurrency}
        formatInteger={formatInteger}
      />

      <CustomCard title="四、保費融資" className={styles.sectionCard}>
        <div style={{ marginBottom: 24, borderRadius: 6 }}>
          <Form.Item
            label={'1. 閣下是否打算運用"保費融資"安排此保單 ? (勾選一項)'}
            name="isPremiumFinanced"
            rules={[{ required: true }]}
          >
            <Radio.Group
              value={data.isPremiumFinanced}
              onChange={(event) =>
                handleChange("isPremiumFinanced", event.target.value)
              }
              style={{ display: "flex", gap: 24 }}
            >
              <Radio value={true}>A. 是</Radio>
              <Radio value={false}>B. 否</Radio>
            </Radio.Group>
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 8, fontSize: 14 }}
            >
              若上方問題之答案為「是」者，請填寫附件"保費融資安排分析"。
            </Text>
          </Form.Item>

          <Form.Item
            label={'2. 閣下是否有任何現有的保單被用作"保費融資"的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品 ? (勾選一項)'}
            style={{ marginTop: 24 }}
            name="hasExistingPolicyAsMortgage"
            rules={[{ required: true }]}
          >
            <Radio.Group
              value={data.hasExistingPolicyAsMortgage}
              onChange={(event) => {
                const value = event.target.value;
                const nextData = {
                  ...data,
                  hasExistingPolicyAsMortgage: value,
                };

                if (
                  value === true &&
                  (!nextData.premiumFinancing ||
                    nextData.premiumFinancing.length === 0)
                ) {
                  nextData.premiumFinancing = [{}];
                }

                onChange(nextData);
              }}
              style={{ display: "flex", gap: 24 }}
            >
              <Radio value={true}>A. 是</Radio>
              <Radio value={false}>B. 否</Radio>
            </Radio.Group>
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 8, fontSize: 14 }}
            >
              若上方問題之答案為「是」者，請填寫下列表格告知現有保費融資的抵押保單詳情。
            </Text>
          </Form.Item>

          {data.hasExistingPolicyAsMortgage && (
            <div style={{ marginTop: 24 }}>
              {(data.premiumFinancing || []).map((item, index) => (
                <div key={index} style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <Text strong style={{ fontSize: 14 }}>
                      保單{index + 1}
                    </Text>
                    {(data.premiumFinancing || []).length > 1 && !readOnly && (
                      <CustomButton
                        bgColor="#ff4d4f"
                        color="#fff"
                        height={24}
                        fontSize={12}
                        onClick={() => onRemovePremiumFinancing(index)}
                      >
                        刪除
                      </CustomButton>
                    )}
                  </div>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item label="保險公司">
                        <SelectInput
                          options={companyList.map((company) => ({
                            label: company.name || company.code,
                            value: company.code,
                          }))}
                          value={item.insuranceCompany}
                          onChange={(value) =>
                            onPremiumFinancingChange(
                              index,
                              "insuranceCompany",
                              value,
                            )
                          }
                          title="保險公司"
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="保單號碼">
                        <Input
                          placeholder="請輸入"
                          value={item.policyNo}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "policyNo",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="貸方名稱">
                        <Input
                          placeholder="請輸入"
                          value={item.lenderName}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "lenderName",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="貸款總額">
                        <Input
                          placeholder="請輸入"
                          value={item.loanAmount}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "loanAmount",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="貸款利率">
                        <Input
                          placeholder="請輸入"
                          suffix="%"
                          value={item.loanInterestRate}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "loanInterestRate",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="貸款期限">
                        <Input
                          placeholder="請輸入"
                          value={item.loanTerm}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "loanTerm",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="還款項">
                        <Input
                          placeholder="請輸入"
                          value={item.repaymentAmount}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "repaymentAmount",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="備註">
                        <Input
                          placeholder="請輸入"
                          value={item.remark}
                          onChange={(event) =>
                            onPremiumFinancingChange(
                              index,
                              "remark",
                              event.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ))}
              {!readOnly && (
                <CustomButton
                  bgColor="var(--theme-primary)"
                  color="#fff"
                  icon={<PlusOutlined />}
                  onClick={onAddPremiumFinancing}
                >
                  新增抵押保單
                </CustomButton>
              )}
            </div>
          )}
        </div>
      </CustomCard>

      <CustomCard title="五、轉保聲明" className={styles.sectionCard}>
        <div style={{ marginBottom: 24, borderRadius: 6 }}>
          <Form.Item
            label="1. 閣下是否使用或打算使用現有人壽保險保單的部分或全部資金，或使用或打算使用通過減少現有人壽保險保單的應付保費而節省的金額，以資助閣下購買新的人壽保險保單？ (勾選一項)"
            name="usingExistingPolicyFundsForNewPolicy"
            rules={[{ required: true }]}
          >
            <Radio.Group
              value={getQuestionValue(
                "transferInsuranceDeclaration",
                "usingExistingPolicyFundsForNewPolicy",
                "RADIO",
              )}
              onChange={(event) =>
                updateQuestionValue(
                  "transferInsuranceDeclaration",
                  "usingExistingPolicyFundsForNewPolicy",
                  event.target.value,
                  "RADIO",
                )
              }
            >
              <Radio value="A">是</Radio>
              <Radio value="B">尚未決定</Radio>
              <Radio value="C">否</Radio>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginLeft: 24, fontSize: 12, color: "#666" }}>
            <p>例如：此等資金或金額可能來自：</p>
            <ul style={{ listStyleType: "none", padding: 0, fontSize: 14 }}>
              <li>
                a) 就閣下現有人壽保險保單作出退保/不分退保的安排，已獲得其退保價值；
              </li>
              <li>
                b) 從閣下現有人壽保險保單中提取保單貸款（包括自動保費貸款/抵押轉讓（不包括保費融資））；
              </li>
              <li>
                c) 從閣下現有人壽保險保單中提取保單價值（例如如實行紅利或贖回基金單位等）；
              </li>
              <li>d) 容許閣下現有人壽保險保單失效（例如終止止付保費）；</li>
              <li>e) 行使閣下現有人壽保險保單中“保費假期”的權利。</li>
            </ul>
          </div>

          <div style={{ color: "red", marginTop: 8, fontSize: 14 }}>
            *請注意，投保之後一年內在冷靜期之後退保的情況，亦有可能涉及轉保，請保險中介人清楚了解情況。
          </div>

          {(getQuestionValue(
            "transferInsuranceDeclaration",
            "usingExistingPolicyFundsForNewPolicy",
            "RADIO",
          ) === "A" ||
            getQuestionValue(
              "transferInsuranceDeclaration",
              "usingExistingPolicyFundsForNewPolicy",
              "RADIO",
            ) === "B") && (
            <div
              style={{
                marginTop: 16,
                padding: 16,
                background: "#f5f5f5",
                borderRadius: 6,
              }}
            >
              <Form.Item
                label="1.A 若上方問題之答案為「是」或「尚未決定」者，保險中介人的評估和意見："
                name="intermediaryAssessmentAndComments"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                  value={getQuestionValue(
                    "transferInsuranceDeclaration",
                    "intermediaryAssessmentAndComments",
                    "RADIO",
                  )}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    const currentDeclaration = setSingleChoiceQuestionInSection(
                      "transferInsuranceDeclaration",
                      "intermediaryAssessmentAndComments",
                      newValue,
                    );

                    if (newValue !== "B") {
                      const otherIndex = currentDeclaration.findIndex(
                        (question) =>
                          question.question ===
                          "intermediaryAssessmentAndCommentsOther",
                      );
                      if (otherIndex >= 0) {
                        currentDeclaration[otherIndex] = {
                          ...currentDeclaration[otherIndex],
                          items: [{ key: "A", value: "" }],
                        };
                      }
                      form.setFieldValue(
                        "intermediaryAssessmentAndCommentsOther",
                        undefined,
                      );
                    }

                    onChange({
                      ...data,
                      transferInsuranceDeclaration: currentDeclaration,
                    });
                  }}
                >
                  <Radio
                    value="A"
                    style={{
                      whiteSpace: "normal",
                      alignItems: "flex-start",
                    }}
                  >
                    A. 在檢視了客戶現有的人壽保險之後，客戶認為新的計劃更為合適(如享有更佳的保障/更靈活的保費繳付模式/較大比例的保證回報/多元化產品特徵)，客人已清楚了解轉保的影響和相關風險
                  </Radio>
                  <Radio value="B">其他</Radio>
                </Radio.Group>
              </Form.Item>

              {getQuestionValue(
                "transferInsuranceDeclaration",
                "intermediaryAssessmentAndComments",
                "RADIO",
              ) === "B" && (
                <Form.Item
                  label="請輸入詳細內容"
                  name="intermediaryAssessmentAndCommentsOther"
                  rules={[{ required: true, message: "請輸入詳細內容" }]}
                >
                  <Input.TextArea
                    placeholder="請輸入"
                    rows={4}
                    value={getQuestionValue(
                      "transferInsuranceDeclaration",
                      "intermediaryAssessmentAndCommentsOther",
                      "INPUT",
                    )}
                    onChange={(event) =>
                      updateQuestionValue(
                        "transferInsuranceDeclaration",
                        "intermediaryAssessmentAndCommentsOther",
                        event.target.value,
                        "INPUT",
                      )
                    }
                  />
                </Form.Item>
              )}
            </div>
          )}
        </div>
      </CustomCard>

      <CustomCard title="六、產品介紹及選擇結果" className={styles.sectionCard}>
        <div ref={productSectionRef}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            {!readOnly && (
              <CustomButton
                bgColor="var(--theme-primary)"
                color="#fff"
                icon={<PlusOutlined />}
                onClick={() => {
                  const nextSelection = [...(data.chooseProducts || [])];
                  nextSelection.push(newChooseProduct);
                  onChange({ ...data, chooseProducts: nextSelection });
                }}
              >
                新增產品
              </CustomButton>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 24, borderRadius: 6 }}>
          <div style={{ fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
            <div style={{ color: "#333", marginBottom: 4 }}>
              曾介紹的保險公司及產品名稱
            </div>
            <div style={{ color: "#ff4d4f" }}>
              *新增介紹的產品後，勾選的產品為最終投保的產品，最多可同時投保 6 個產品，如只是新增作介紹對比的產品而最終不購買，請勿勾選。
            </div>
          </div>

          <Row gutter={[24, 24]}>
            {(() => {
              const getDerivedFields = () => {
                const objectives =
                  getQuestionValue(
                    "financialAnalysis",
                    "insurancePurchaseObjectives",
                    "CHECKBOX",
                  ) || [];
                const objectiveLabels: string[] = [];

                if (objectives.includes("A")) {
                  objectiveLabels.push("為應付不時之需提供財務保障");
                }
                if (objectives.includes("B")) {
                  objectiveLabels.push("為醫療需要作準備");
                }
                if (objectives.includes("C")) {
                  objectiveLabels.push("為未來提供定期的收入");
                }
                if (objectives.includes("D")) {
                  objectiveLabels.push("為未來需要做儲蓄");
                }
                if (objectives.includes("E")) {
                  objectiveLabels.push("投資");
                }
                if (objectives.includes("F")) {
                  objectiveLabels.push("其他");
                }

                const objective = objectiveLabels.join("、");
                const paymentRange = getQuestionValue(
                  "financialAnalysis",
                  "willingPremiumPaymentPeriodRange",
                  "RADIO",
                );
                let paymentTerm = "";

                if (paymentRange) {
                  const paymentTermMap: Record<string, string> = {
                    A: "2-5年",
                    B: "6-10年",
                    C: "11-15年",
                    D: "16-20年",
                    E: "超過20年",
                    F: "終身",
                  };

                  if (paymentRange === "G") {
                    const singlePaymentLimit = getQuestionValue(
                      "financialAnalysis",
                      "singlePremiumPaymentLimit",
                      "INPUT",
                    );
                    const formattedAmount = singlePaymentLimit
                      ? Number(singlePaymentLimit).toLocaleString("en-US")
                      : "0";
                    paymentTerm = `一次性付款不超過 ${formattedAmount} HKD`;
                  } else {
                    paymentTerm = paymentTermMap[paymentRange] || "";
                  }
                }

                const benefitTermMap: Record<string, string> = {
                  A: "少於1年",
                  B: "1-5年",
                  C: "6-10年",
                  D: "11-15年",
                  E: "16-20年",
                  F: "超過20年",
                  G: "終身",
                };
                const policyTargetExpectedTime = getQuestionValue(
                  "financialAnalysis",
                  "policyTargetExpectedTime",
                  "RADIO",
                );
                const benefitTerm = policyTargetExpectedTime
                  ? benefitTermMap[policyTargetExpectedTime]
                  : undefined;

                return { objective, paymentTerm, benefitTerm };
              };

              const derived = getDerivedFields();
              const productsToShow = [...(data.chooseProducts || [])];
              const selectedCount = productsToShow.filter(
                (product) => product.choose,
              ).length;

              if (productsToShow.length === 0) {
                return (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: "#999",
                    }}
                  >
                    暫無產品，請點擊上方「新增產品」按鈕添加
                  </div>
                );
              }

              return (
                <>
                  {productsToShow.map((product, index) => {
                    const validation = productValidation[index] || {
                      paymentTermValid: true,
                      benefitTermValid: true,
                    };
                    const handleToggleSelect = (isChecking: boolean) => {
                      if (readOnly) return;
                      if (isChecking && selectedCount >= 6) {
                        onMessageError("最多只能選擇6個產品");
                        return;
                      }

                      const nextSelection = [...(data.chooseProducts || [])];
                      if (!nextSelection[index]) {
                        nextSelection[index] = newChooseProduct;
                      }

                      nextSelection[index] = {
                        ...nextSelection[index],
                        choose: isChecking,
                      };

                      const productsWithCompany = nextSelection.filter(
                        (item) => item.companyCode,
                      );
                      const uniqueCompanies = new Set(
                        productsWithCompany.map((item) => item.companyCode),
                      );
                      const isSameCompany =
                        productsWithCompany.length > 0 &&
                        uniqueCompanies.size === 1;

                      onChange({
                        ...data,
                        chooseProducts: nextSelection,
                        onlyProvideOneProduct: isSameCompany,
                      });
                    };

                    return (
                      <Col span={24} key={index}>
                        <CustomCard
                          onClick={(event) => {
                            const target = event.target as HTMLElement;
                            if (
                              target.closest(".ant-checkbox-wrapper") ||
                              target.closest(".ant-checkbox")
                            ) {
                              return;
                            }
                            handleToggleSelect(!product.choose);
                          }}
                          data-invalid={
                            !validation.paymentTermValid ||
                            !validation.benefitTermValid
                          }
                          title={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <Checkbox
                                checked={product.choose}
                                disabled={readOnly}
                                onChange={(event) => {
                                  event.stopPropagation();
                                  handleToggleSelect(event.target.checked);
                                }}
                              />
                              <span>介紹的產品{index + 1}</span>
                            </div>
                          }
                          extra={
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              }}
                            >
                              {product.choose && (
                                <CustomButton
                                  bgColor="var(--theme-primary)"
                                  height={24}
                                  fontSize={12}
                                >
                                  已選擇
                                </CustomButton>
                              )}
                              {(data.chooseProducts?.length || 0) > 1 &&
                                !readOnly && (
                                  <CustomButton
                                    bgColor="#ff4d4f"
                                    color="#fff"
                                    height={24}
                                    fontSize={12}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      const nextSelection = [
                                        ...(data.chooseProducts || []),
                                      ];
                                      nextSelection.splice(index, 1);
                                      onChange({
                                        ...data,
                                        chooseProducts: nextSelection,
                                      });
                                    }}
                                  >
                                    刪除
                                  </CustomButton>
                                )}
                            </div>
                          }
                          style={{
                            border: product.choose
                              ? "1px solid var(--theme-primary)"
                              : "1px solid #d9d9d9",
                          }}
                        >
                          <Row gutter={24}>
                            <Col span={12}>
                              <Form.Item label="產品計劃">
                                {readOnly ? (
                                  <Input
                                    readOnly
                                    className={styles.derivedFieldInput}
                                    value={product.productName || ""}
                                    placeholder="請選擇"
                                    suffix={
                                      <DownOutlined
                                        className={styles.derivedFieldIcon}
                                      />
                                    }
                                  />
                                ) : (
                                  <div
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <ProductSelectInput
                                      value={product.productCode || ""}
                                      initialName={product.productName}
                                      context={{ type: "financial", index }}
                                      onOpenSelector={onOpenSelector}
                                      disabled={readOnly}
                                    />
                                  </div>
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="選購產品目標">
                                <Input
                                  disabled
                                  value={derived.objective}
                                  placeholder="自動生成"
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="選購產品的繳付年期">
                                <Input
                                  disabled
                                  value={derived.paymentTerm}
                                  placeholder="自動生成"
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="目標得益/保障年期">
                                <Input
                                  disabled
                                  value={derived.benefitTerm}
                                  placeholder="自動生成"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </CustomCard>
                      </Col>
                    );
                  })}
                </>
              );
            })()}
          </Row>

          <Divider style={{ margin: "24px 0" }} />

          {(() => {
            const allProducts = data.chooseProducts || [];
            const productsWithCompany = allProducts.filter(
              (product) => product.companyCode,
            );
            const uniqueCompanies = new Set(
              productsWithCompany.map((product) => product.companyCode),
            );
            const shouldShowReason =
              productsWithCompany.length > 0 && uniqueCompanies.size === 1;

            return shouldShowReason ? (
              <Form.Item
                label="請在下方填寫只能提供一間保險公司建議之原因："
                name="onlyProvideOneProductReason"
                rules={[{ required: true, message: "請輸入原因" }]}
              >
                <Input
                  placeholder="請輸入"
                  value={data.onlyProvideOneProductReason}
                  onChange={(event) =>
                    handleChange(
                      "onlyProvideOneProductReason",
                      event.target.value,
                    )
                  }
                />
              </Form.Item>
            ) : null;
          })()}
        </div>
      </CustomCard>

      <CustomCard title="七、建議及原因" className={styles.sectionCard}>
        <div style={{ marginBottom: 24, borderRadius: 6 }}>
          <Form.Item
            label="1. 有沒有出現合適性偏差？"
            name="suitabilityDeviationOccurred"
            rules={[{ required: true }]}
          >
            <Radio.Group
              value={getQuestionValue(
                "suggestionsAndReasons",
                "suitabilityDeviationOccurred",
                "RADIO",
              )}
              onChange={(event) => {
                const newValue = event.target.value;
                const currentSuggestions = setSingleChoiceQuestionInSection(
                  "suggestionsAndReasons",
                  "suitabilityDeviationOccurred",
                  newValue,
                );

                const clearKeys =
                  newValue === "A"
                    ? ["suitabilityDeviationFollowUpNoOptions"]
                    : [
                        "suitabilityDeviationFollowUpYesOptions",
                        "reasonForRecommendingDespiteDeviation",
                        "otherReasonForRecommendation",
                      ];

                clearKeys.forEach((key) => {
                  const questionIndex = currentSuggestions.findIndex(
                    (question) => question.question === key,
                  );
                  if (questionIndex >= 0) {
                    const isInput = key === "otherReasonForRecommendation";
                    currentSuggestions[questionIndex] = {
                      ...currentSuggestions[questionIndex],
                      items: isInput
                        ? [{ key: "A", value: "" }]
                        : (currentSuggestions[questionIndex].items || []).map(
                            (item: any) => ({
                              ...item,
                              value: false,
                            }),
                          ),
                    };
                  }
                  form.setFieldValue(key, undefined);
                });

                onChange({
                  ...data,
                  suggestionsAndReasons: currentSuggestions,
                });
              }}
            >
              <Radio value="A">有</Radio>
              <Radio value="B">沒有</Radio>
            </Radio.Group>
          </Form.Item>

          {getQuestionValue(
            "suggestionsAndReasons",
            "suitabilityDeviationOccurred",
            "RADIO",
          ) === "A" && (
            <div style={{ marginTop: 16 }}>
              <Form.Item
                label="1.1 若上方問題之答案為「有」者，請選擇原因，（勾選一項）："
                name="suitabilityDeviationFollowUpYesOptions"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                  value={getQuestionValue(
                    "suggestionsAndReasons",
                    "suitabilityDeviationFollowUpYesOptions",
                    "RADIO",
                  )}
                  onChange={(event) =>
                    updateQuestionValue(
                      "suggestionsAndReasons",
                      "suitabilityDeviationFollowUpYesOptions",
                      event.target.value,
                      "RADIO",
                    )
                  }
                >
                  <Radio value="A">a. 購買保險產品的目標</Radio>
                  <Radio value="B">b. 目標利益/保障期的預期時間</Radio>
                  <Radio value="C">c. 能夠及願意繳付的保費佔所有收入來源</Radio>
                  <Radio value="D">d. 能夠及願意為保單支付保費的年期</Radio>
                  <Radio value="E">e. 保障所需要的金額</Radio>
                  <Radio
                    value="F"
                    style={{
                      whiteSpace: "normal",
                      alignItems: "flex-start",
                    }}
                  >
                    f. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出，在與申請人商討後，申請人期望在以上各方面及以下原因取得平衡。
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          )}

          {getQuestionValue(
            "suggestionsAndReasons",
            "suitabilityDeviationOccurred",
            "RADIO",
          ) === "B" && (
            <div style={{ marginTop: 16 }}>
              <Form.Item
                label="1.2 若上方問題之答案為「沒有」者，請選擇原因，（勾選一項）："
                name="suitabilityDeviationFollowUpNoOptions"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                  value={getQuestionValue(
                    "suggestionsAndReasons",
                    "suitabilityDeviationFollowUpNoOptions",
                    "RADIO",
                  )}
                  onChange={(event) =>
                    updateQuestionValue(
                      "suggestionsAndReasons",
                      "suitabilityDeviationFollowUpNoOptions",
                      event.target.value,
                      "RADIO",
                    )
                  }
                >
                  <Radio
                    value="A"
                    style={{
                      whiteSpace: "normal",
                      alignItems: "flex-start",
                    }}
                  >
                    a. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出
                  </Radio>
                  <Radio
                    value="B"
                    style={{
                      whiteSpace: "normal",
                      alignItems: "flex-start",
                    }}
                  >
                    b. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出，在與申請人商討後，申請人期望在各方面取得平衡
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          )}

          {getQuestionValue(
            "suggestionsAndReasons",
            "suitabilityDeviationOccurred",
            "RADIO",
          ) === "A" && (
            <Form.Item
              label="2. 雖然有合適性偏差出現，但仍然介紹此產品的原因是：（勾選一項）"
              name="reasonForRecommendingDespiteDeviation"
              rules={[{ required: true }]}
            >
              <Radio.Group
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
                value={getQuestionValue(
                  "suggestionsAndReasons",
                  "reasonForRecommendingDespiteDeviation",
                  "RADIO",
                )}
                onChange={(event) => {
                  const newValue = event.target.value;
                  const currentSuggestions = setSingleChoiceQuestionInSection(
                    "suggestionsAndReasons",
                    "reasonForRecommendingDespiteDeviation",
                    newValue,
                  );

                  if (newValue !== "B") {
                    const otherIndex = currentSuggestions.findIndex(
                      (question) =>
                        question.question === "otherReasonForRecommendation",
                    );
                    if (otherIndex >= 0) {
                      currentSuggestions[otherIndex] = {
                        ...currentSuggestions[otherIndex],
                        items: [{ key: "A", value: "" }],
                      };
                    }
                    form.setFieldValue(
                      "otherReasonForRecommendation",
                      undefined,
                    );
                  }

                  onChange({
                    ...data,
                    suggestionsAndReasons: currentSuggestions,
                  });
                }}
              >
                <Radio
                  value="A"
                  style={{
                    whiteSpace: "normal",
                    alignItems: "flex-start",
                  }}
                >
                  A. 因現在供款預算原因，這保險計劃的金額未能達到保險或儲蓄的全面需求，但現在建議的保險，已經符合年期及供款金額等要求，能夠邁向至全面的保障及儲蓄目標
                </Radio>
                <Radio value="B">其他</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {getQuestionValue(
            "suggestionsAndReasons",
            "suitabilityDeviationOccurred",
            "RADIO",
          ) === "A" &&
            getQuestionValue(
              "suggestionsAndReasons",
              "reasonForRecommendingDespiteDeviation",
              "RADIO",
            ) === "B" && (
              <div style={{ marginTop: 16 }}>
                <Form.Item
                  label="3. 其他原因"
                  name="otherReasonForRecommendation"
                  rules={[{ required: true, message: "請輸入原因" }]}
                >
                  <Input
                    value={getQuestionValue(
                      "suggestionsAndReasons",
                      "otherReasonForRecommendation",
                      "INPUT",
                    )}
                    onChange={(event) =>
                      updateQuestionValue(
                        "suggestionsAndReasons",
                        "otherReasonForRecommendation",
                        event.target.value,
                        "INPUT",
                      )
                    }
                    placeholder="請輸入"
                  />
                </Form.Item>
              </div>
            )}
        </div>
      </CustomCard>
    </Form>
  );
}
